import React, { useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Switch } from "@/components/ui/switch";
import { Sprout, FlaskConical, Layers3 } from "lucide-react";
import { Card, Stat, CHART_COLORS, tooltipStyle } from "./shared";

const AMENDMENTS = [
    { id: "leach", label: "Leach perchlorates (water rinse)", gain: 30 },
    { id: "organic", label: "Add compost / biochar", gain: 20 },
    { id: "buffer", label: "Buffer pH toward 6.5", gain: 10 },
    { id: "irrigate", label: "Irrigate from local ice", gain: 15 },
];

export function baseSuitability(s) {
    let score = 40;
    score -= Math.min(30, s.perchlorate_wt_pct * 40);
    score -= Math.abs(s.ph - 6.5) * 5;
    score += Math.min(15, s.water_content_pct * 0.6);
    score += Math.min(10, s.organics_ppm / 3);
    return Math.max(0, Math.round(score));
}

function SuitabilityCalc({ soil }) {
    const [on, setOn] = useState({});
    const base = baseSuitability(soil);
    const score = Math.min(100, base + AMENDMENTS.reduce((a, m) => a + (on[m.id] ? m.gain : 0), 0));
    const tone = score >= 70 ? "text-ares-emerald" : score >= 40 ? "text-ares-amber" : "text-ares-orange";
    return (
        <div>
            <div className="flex items-end gap-2 mb-1">
                <span className={`font-heading text-4xl font-bold ${tone}`} data-testid="soil-suitability-score">{score}</span>
                <span className="text-ares-muted mb-1.5 text-sm">/ 100 plant-growth suitability</span>
            </div>
            <div className="h-2 rounded-full bg-ares-void overflow-hidden mb-4">
                <div className="h-full bg-ares-emerald transition-[width] duration-500" style={{ width: `${score}%` }} />
            </div>
            <p className="text-xs text-ares-muted mb-3">Raw regolith scores {base}. Toggle habitat amendments:</p>
            <div className="space-y-2.5">
                {AMENDMENTS.map((m) => (
                    <label key={m.id} className="flex items-center justify-between gap-3 text-sm cursor-pointer">
                        <span>{m.label} <span className="font-mono text-[10px] text-ares-emerald">+{m.gain}</span></span>
                        <Switch checked={!!on[m.id]} onCheckedChange={(v) => setOn((o) => ({ ...o, [m.id]: v }))} data-testid={`soil-amend-${m.id}`} />
                    </label>
                ))}
            </div>
        </div>
    );
}

export default function SoilPanel({ data }) {
    const s = data.soil;
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card title="Regolith chemistry" icon={FlaskConical} testid="soil-chem-card" className="lg:col-span-2">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <Stat label="pH" value={s.ph} testid="soil-ph" />
                    <Stat label="Perchlorate" value={s.perchlorate_wt_pct} unit="wt%" testid="soil-perchlorate" />
                    <Stat label="Organics" value={s.organics_ppm} unit="ppm" testid="soil-organics" />
                    <Stat label="Bulk density" value={s.bulk_density} unit="g/cm³" testid="soil-density" />
                    <Stat label="Water" value={s.water_content_pct} unit="wt%" testid="soil-water" />
                </div>
                <div className="mt-5 space-y-1.5" data-testid="soil-horizons">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted mb-2 flex items-center gap-1.5"><Layers3 className="w-3.5 h-3.5" /> Soil profile (pedology)</p>
                    {s.horizons.map((h, i) => (
                        <div key={i} className="flex items-center gap-3 rounded-md border border-ares-border bg-ares-panel/60 px-3 py-2" style={{ opacity: 1 - i * 0.12 }}>
                            <span className="font-mono text-[11px] text-ares-orange w-16 shrink-0">{h.depth_cm} cm</span>
                            <span className="text-sm font-medium w-36 shrink-0">{h.name}</span>
                            <span className="text-xs text-ares-muted">{h.desc}</span>
                        </div>
                    ))}
                </div>
            </Card>
            <Card title="Texture" testid="soil-texture-card">
                <div style={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={data.grain_size} dataKey="pct" nameKey="type" innerRadius={45} outerRadius={75} paddingAngle={3}>
                                {data.grain_size.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                            </Pie>
                            <Tooltip {...tooltipStyle} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-3 justify-center">
                    {data.grain_size.map((g, i) => (
                        <span key={g.type} className="text-xs flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i] }} />{g.type} {g.pct}%</span>
                    ))}
                </div>
            </Card>
            <Card title="Edaphology · crop-growth simulator" icon={Sprout} accent="text-ares-emerald" testid="soil-suitability-card" className="lg:col-span-3">
                <SuitabilityCalc key={data.region_id} soil={s} />
            </Card>
        </div>
    );
}
